import { useMemo, useRef, type ReactNode, type RefObject } from "react";
import { isNil } from "es-toolkit";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { getAnimation } from "@/entities/exercise/model/animations";
import type { ExerciseId } from "@/shared/domain/ids";
import type { LimbPivots } from "@/entities/muscle/ui/limb-rigging.utils";

const applyJointRotation = (
  pivot: THREE.Object3D | undefined,
  rotation: { x?: number; y?: number; z?: number } | undefined,
): void => {
  if (isNil(pivot)) return;

  pivot.rotation.x = rotation?.x ?? 0;
  pivot.rotation.y = rotation?.y ?? 0;
  pivot.rotation.z = rotation?.z ?? 0;
};

const applyJointRotations = (
  pivots: LimbPivots,
  joints: ReturnType<ReturnType<typeof getAnimation>>,
): void => {
  applyJointRotation(pivots["lShoulder"], joints.lShoulder);
  applyJointRotation(pivots["rShoulder"], joints.rShoulder);
  applyJointRotation(pivots["lElbow"], joints.lElbow);
  applyJointRotation(pivots["rElbow"], joints.rElbow);
  applyJointRotation(pivots["lHip"], joints.lHip);
  applyJointRotation(pivots["rHip"], joints.rHip);
  applyJointRotation(pivots["lKnee"], joints.lKnee);
  applyJointRotation(pivots["rKnee"], joints.rKnee);
};

const computeFit = (clonedScene: THREE.Object3D) => {
  const box = new THREE.Box3().setFromObject(clonedScene);
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  box.getSize(size);
  box.getCenter(center);
  const targetHeight = 6.5;
  const uniform = size.y > 0 ? targetHeight / size.y : 1;

  return {
    scale: [uniform, uniform, uniform] as const,
    position: [-center.x * uniform, -box.min.y * uniform, -center.z * uniform] as const,
  };
};

type AnatomyRigProps = Readonly<{
  clonedScene: THREE.Object3D;
  exerciseId: ExerciseId;
  animate: boolean;
  pivotsRef: RefObject<LimbPivots>;
  children: ReactNode;
}>;

export const AnatomyRig = ({
  clonedScene,
  exerciseId,
  animate,
  pivotsRef,
  children,
}: AnatomyRigProps) => {
  const animationRootRef = useRef<THREE.Group>(null);
  const invalidate = useThree((state) => state.invalidate);

  // Compute once on the un-mounted clone. After the first commit, clonedScene is
  // inside the parent group below — setFromObject would then return world-space
  // bounds that already include our uniformScale, creating a feedback loop where
  // each re-render rescales the model.
  const { scale, position } = useMemo(() => computeFit(clonedScene), [clonedScene]);

  useFrame((state) => {
    const root = animationRootRef.current;
    if (isNil(root)) return;

    const animation = animate ? getAnimation(exerciseId) : getAnimation("default");
    const joints = animation(state.clock.elapsedTime);
    const frameScale = scale[1];

    root.position.y = (joints.rootY ?? 0) * frameScale;
    root.rotation.x = (joints.spine?.x ?? 0) * 0.6;
    root.rotation.y = (joints.spine?.y ?? 0) * 0.5;

    if (!animate) return;

    applyJointRotations(pivotsRef.current, joints);
    invalidate();
  });

  return (
    <group position={position} scale={scale}>
      <group ref={animationRootRef}>{children}</group>
    </group>
  );
};
